import './commitment-details-sheet.scss';

import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';
import { ActionCard } from '@sola/ark-core/molecules/action-card/action-card.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from '@sola/ark-core/molecules/sheet/sheet.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import {
  GraphQLCommitment,
  GraphQLCommitmentType,
  GraphQLPledge,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.js';
import { paymentMethodsQuery } from '@sola/supporter-sdk/queries.js';
import {
  getCommitmentLabel,
  getCommitmentTitle,
  getFrequencyAbbr,
} from '@sola/supporter-sdk/utils/commitments.js';

import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar';
import { PledgeIcon } from '../../atoms/pledge-icon/pledge-icon';
import { LocalAlert } from '../../molecules/local-alerts/local-alerts';
import {
  LocalAlertsProvider,
  useLocalAlerts,
} from '../../molecules/local-alerts/local-alerts-provider';
import useModalAlerts from '../../molecules/local-alerts/useModalAlerts';
import { SectionContent } from '../../molecules/section-content/section-content';
import { formatPledgeName } from '../../utils/pledges';
import { CommitmentDetailsPaymentPlan } from './commitment-details-payment-plan';

export interface CommitmentDetailsSheetProps {
  /**
   * This should only take in elements that resolve to html buttons.
   */
  isOpen?: boolean;
  onOpenChange?: (open: boolean, commitmentId: string) => void;
  openTrigger: ReactElement;
  commitment?: GraphQLCommitment | null;
  pledge?: GraphQLPledge | null;
}

export interface CommitmentDetailsProps {
  commitment: GraphQLCommitment;
}

export interface PledgeDetailsProps {
  pledge: GraphQLPledge;
}

interface CommitmentDetailsListItemProps {
  title: string;
  children: React.ReactNode;
}

export interface CommitmentDetailsSectionProps {
  title?: string;
  children: React.ReactNode;
}

interface CommitmentDetailsBodyProps {
  title: string;
  children: React.ReactNode;
}

const CommitmentDetailsListItem: React.FC<CommitmentDetailsListItemProps> = ({
  title,
  children,
}) => {
  return (
    <li className="label-md label-moderate label-neutral-mild">
      {title && (
        <span className="label-potent">
          {title}
          {': '}
        </span>
      )}
      <span>{children}</span>
    </li>
  );
};

export const CommitmentDetailsSection: React.FC<
  CommitmentDetailsSectionProps
> = ({ title, children }) => {
  return (
    <div className="CommitmentDetails__section py-comfy-xl">
      <SectionContent heading={title || ''}>
        <div className="flex flex-col gap-comfy-sm">{children}</div>
      </SectionContent>
    </div>
  );
};

export const CommitmentDetailsActionCard: React.FC = () => {
  const { t } = useTranslation(['my']);

  return (
    <div className="pt-comfy-xl">
      <ActionCard
        icon={<ArkIcon iconName="message-question-square" size="18" />}
        title={t('commitments.details.needHelp.title')}
        subtitle={t('commitments.details.needHelp.description')}
        href={t('ctas.contactSupport.url')}
      />
    </div>
  );
};

const CommitmentDetailsBody: React.FC<
  React.PropsWithChildren<CommitmentDetailsBodyProps>
> = ({ title, children }) => {
  return (
    <div className="CommitmentDetails flex flex-col">
      <h3 className="CommitmentDetails__title body-sm body-neutral-mild pb-comfy-xl">
        {title}
      </h3>
      {children}
    </div>
  );
};

export const PledgeDetails: React.FC<PledgeDetailsProps> = ({
  pledge,
}: PledgeDetailsProps) => {
  const { t } = useTranslation('my');

  return (
    <>
      <CommitmentDetailsSection
        title={t('commitments.category.financialPledges')}
      >
        {pledge?.pledgeName && (
          <>
            <PledgeIcon pledgeName={pledge.pledgeName} />

            <span className="label-lg label-potent">
              {formatPledgeName(pledge.pledgeName, t)}
            </span>
          </>
        )}

        <ul className="p-0 list-none m-0 CommitmentDetails__overview flex flex-col gap-snug-md">
          <CommitmentDetailsListItem
            title={t('commitments.pledges.details.totalAmount')}
          >
            {t('commitments.pledges.details.values.amount', {
              amount: pledge.totalPledgeAmount,
            })}
          </CommitmentDetailsListItem>
          <CommitmentDetailsListItem
            title={t('commitments.pledges.details.totalGiven')}
          >
            {t('commitments.pledges.details.values.amount', {
              amount: pledge.totalGivenToDate,
            })}
          </CommitmentDetailsListItem>
          <CommitmentDetailsListItem
            title={t('commitments.pledges.details.amountOutstanding')}
          >
            {t('commitments.pledges.details.values.amount', {
              amount: pledge.outstandingAmount,
            })}
          </CommitmentDetailsListItem>
        </ul>
      </CommitmentDetailsSection>

      {pledge?.lastInstallmentDate && pledge?.lastInstallmentAmount && (
        <CommitmentDetailsSection
          title={t('commitments.pledges.details.lastInstallment')}
        >
          <ul className="p-0 list-none m-0 CommitmentDetails__overview flex flex-col gap-snug-md">
            <CommitmentDetailsListItem
              title={t('commitments.pledges.details.installmentDate')}
            >
              {t('commitments.pledges.details.values.installmentDate', {
                date: pledge?.lastInstallmentDate,
              })}
            </CommitmentDetailsListItem>
            <CommitmentDetailsListItem
              title={t('commitments.pledges.details.amount')}
            >
              {t('commitments.pledges.details.values.amount', {
                amount: pledge?.lastInstallmentAmount,
              })}
            </CommitmentDetailsListItem>
          </ul>
        </CommitmentDetailsSection>
      )}

      {pledge?.nextInstallmentDate && pledge?.nextInstallmentAmount && (
        <CommitmentDetailsSection
          title={t('commitments.pledges.details.nextInstallment')}
        >
          <ul className="p-0 list-none m-0 CommitmentDetails__overview flex flex-col gap-snug-md">
            <CommitmentDetailsListItem
              title={t('commitments.pledges.details.installmentDate')}
            >
              {t('commitments.pledges.details.values.installmentDate', {
                date: pledge?.nextInstallmentDate,
              })}
            </CommitmentDetailsListItem>
            <CommitmentDetailsListItem
              title={t('commitments.pledges.details.amount')}
            >
              {t('commitments.pledges.details.values.amount', {
                amount: pledge?.nextInstallmentAmount,
              })}
            </CommitmentDetailsListItem>
          </ul>
        </CommitmentDetailsSection>
      )}
    </>
  );
};

export const CommitmentDetails: React.FC<CommitmentDetailsProps> = ({
  commitment,
}: CommitmentDetailsProps) => {
  const { t } = useTranslation(['my', 'common']);
  const { alerts, removeAlert } = useLocalAlerts();

  const { data, isFetching, isLoading, isPending, isError } =
    useSupporterQuery(paymentMethodsQuery);

  const isShowingSkeletons = (isLoading || isFetching || isPending) && !isError;

  return (
    <>
      {alerts.length > 0 ? (
        <div>
          {alerts.map((alert) => (
            <LocalAlert
              key={alert.id}
              alert={alert}
              className="mb-comfy-sm"
              onClose={removeAlert}
            />
          ))}
        </div>
      ) : undefined}
      <CommitmentDetailsSection title={getCommitmentLabel(commitment, t)}>
        <CommitmentAvatar size="lg" commitment={commitment} />
        <span className="label-lg label-potent">
          {getCommitmentTitle(commitment, t)}
        </span>

        {/* TODO: Add alert here for assigning payment method to commitment. */}

        <ul className="p-0 list-none m-0 CommitmentDetails__overview flex flex-col gap-snug-md">
          {commitment.commitmentType ===
            GraphQLCommitmentType.ChildSponsorship && (
            <CommitmentDetailsListItem
              title={t('commitments.details.overview.list.id')}
            >
              {commitment.participant?.localId}
            </CommitmentDetailsListItem>
          )}
          <CommitmentDetailsListItem
            title={t('commitments.details.overview.list.amount')}
          >
            {t('common:numbers.currencyCustom', {
              amount: commitment.rate,
            })}
            <span>{getFrequencyAbbr(commitment.frequency || '', t)}</span>
          </CommitmentDetailsListItem>
          {!!commitment.isRecurring && (
            <CommitmentDetailsListItem
              title={t('commitments.details.overview.list.nextPayment')}
            >
              {commitment.paymentDateNext
                ? t('commitments.details.overview.values.nextPayment', {
                    date: commitment.paymentDateNext,
                  })
                : t('commitments.statuses.pending')}
            </CommitmentDetailsListItem>
          )}
        </ul>
      </CommitmentDetailsSection>

      <CommitmentDetailsSection
        title={t('commitments.details.paymentMethod.title')}
      >
        {isShowingSkeletons && !data && <Skeleton height={100} width={250} />}
        {!isShowingSkeletons && isError && (
          <Alert
            variant="negative"
            title={t('commitments.details.paymentMethod.queryError.title')}
            message={t('commitments.details.paymentMethod.queryError.message')}
            action={
              <a href={t('ctas.contactSupport.url')}>
                {t('ctas.contactSupport.text')}
              </a>
            }
          />
        )}
        {!isShowingSkeletons && !isError && (
          <CommitmentDetailsPaymentPlan commitment={commitment} />
        )}
      </CommitmentDetailsSection>

      {commitment.paymentPullDay && (
        <CommitmentDetailsSection
          title={t('commitments.details.paymentDay.title')}
        >
          <span className="label-moderate label-md label-neutral-mild">
            {t('commitments.details.paymentDay.paymentPullDayLabel', {
              count: commitment.paymentPullDay,
              ordinal: true,
            })}
          </span>
        </CommitmentDetailsSection>
      )}

      <CommitmentDetailsActionCard />
    </>
  );
};

export const CommitmentDetailsSheet: React.FC<
  React.PropsWithChildren<CommitmentDetailsSheetProps>
> = ({ isOpen = false, openTrigger, commitment, pledge, onOpenChange }) => {
  const { t } = useTranslation('my');
  const title = t('commitments.detailsSheet.heading');
  const { alerts: sheetAlerts, removeAlert: removeSheetAlert } =
    useModalAlerts();

  if (!commitment && !pledge) {
    return null;
  }

  return (
    <Sheet
      {...(isOpen !== undefined && { open: isOpen })}
      {...(onOpenChange &&
        commitment && {
          onOpenChange: (open) => {
            onOpenChange(open, commitment.internalId);
          },
        })}
      {...(onOpenChange &&
        pledge?.pledgeId && {
          onOpenChange: (open) => {
            onOpenChange(open, pledge.pledgeId!);
          },
        })}
    >
      <SheetTrigger>{openTrigger}</SheetTrigger>
      <SheetContent title={title}>
        <SheetHeader>{title}</SheetHeader>
        <SheetBody>
          <LocalAlertsProvider>
            {sheetAlerts.length > 0 && (
              <div className="mt-comfy-sm md:mt-comfy-lg">
                {sheetAlerts.map((sheetAlert) => (
                  <LocalAlert
                    key={sheetAlert.id}
                    alert={sheetAlert}
                    className="my-comfy-xs"
                    onClose={removeSheetAlert}
                  />
                ))}
              </div>
            )}
            <CommitmentDetailsBody
              title={
                commitment
                  ? t('commitments.details.subTitle')
                  : t('commitments.pledges.details.subTitle', {
                      name: pledge?.pledgeName,
                    })
              }
            >
              {commitment && <CommitmentDetails commitment={commitment} />}
              {pledge && <PledgeDetails pledge={pledge} />}
            </CommitmentDetailsBody>
          </LocalAlertsProvider>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
};
